/**
 * Filter Types for R4S OmniMens Widget Filtering
 * Supports flexible field-based filtering with type-aware operators
 */

// Field types that determine available operators
export type FilterFieldType = 'string' | 'number' | 'date' | 'boolean' | 'unknown';

// Operators for different field types
export type StringOperator = 'equals' | 'not_equals' | 'contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty';
export type NumberOperator = 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'greater_or_equal' | 'less_or_equal' | 'between';
export type DateOperator = 'equals' | 'before' | 'after' | 'between' | 'last_n_days' | 'today' | 'this_week' | 'this_month';
export type BooleanOperator = 'is_true' | 'is_false';

export type FilterOperator = StringOperator | NumberOperator | DateOperator | BooleanOperator;

// Single filter condition
export interface FilterCondition {
  id: string;
  field: string;
  fieldType: FilterFieldType;
  operator: FilterOperator;
  value: string | number | boolean | null;
  value2?: string | number | null; // For 'between' operator
}

// Filter group with AND/OR logic
export interface FilterGroup {
  id: string;
  logic: 'and' | 'or';
  conditions: FilterCondition[];
}

// Complete widget filter configuration
export interface WidgetFilterConfig {
  enabled: boolean;
  groups: FilterGroup[];
  // Quick filters are predefined shortcuts
  quickFilters?: QuickFilter[];
}

// Quick filter presets
export interface QuickFilter {
  id: string;
  label: string;
  icon?: string;
  conditions: FilterCondition[];
}

// Operator configuration for UI
export interface OperatorConfig {
  value: FilterOperator;
  label: string;
  requiresValue: boolean;
  requiresSecondValue?: boolean;
  valueType?: 'text' | 'number' | 'date' | 'select';
}

// Field info for filter UI
export interface FilterableField {
  name: string;
  label: string;
  type: FilterFieldType;
  sampleValues?: string[];
}

// Operators by field type
export const STRING_OPERATORS: OperatorConfig[] = [
  { value: 'equals', label: 'equals', requiresValue: true, valueType: 'text' },
  { value: 'not_equals', label: 'not equals', requiresValue: true, valueType: 'text' },
  { value: 'contains', label: 'contains', requiresValue: true, valueType: 'text' },
  { value: 'starts_with', label: 'starts with', requiresValue: true, valueType: 'text' },
  { value: 'ends_with', label: 'ends with', requiresValue: true, valueType: 'text' },
  { value: 'is_empty', label: 'is empty', requiresValue: false },
  { value: 'is_not_empty', label: 'is not empty', requiresValue: false },
];

export const NUMBER_OPERATORS: OperatorConfig[] = [
  { value: 'equals', label: '=', requiresValue: true, valueType: 'number' },
  { value: 'not_equals', label: '≠', requiresValue: true, valueType: 'number' },
  { value: 'greater_than', label: '>', requiresValue: true, valueType: 'number' },
  { value: 'less_than', label: '<', requiresValue: true, valueType: 'number' },
  { value: 'greater_or_equal', label: '≥', requiresValue: true, valueType: 'number' },
  { value: 'less_or_equal', label: '≤', requiresValue: true, valueType: 'number' },
  { value: 'between', label: 'between', requiresValue: true, requiresSecondValue: true, valueType: 'number' },
];

export const DATE_OPERATORS: OperatorConfig[] = [
  { value: 'equals', label: 'equals', requiresValue: true, valueType: 'date' },
  { value: 'before', label: 'before', requiresValue: true, valueType: 'date' },
  { value: 'after', label: 'after', requiresValue: true, valueType: 'date' },
  { value: 'between', label: 'between', requiresValue: true, requiresSecondValue: true, valueType: 'date' },
  { value: 'last_n_days', label: 'last X days', requiresValue: true, valueType: 'number' },
  { value: 'today', label: 'today', requiresValue: false },
  { value: 'this_week', label: 'this week', requiresValue: false },
  { value: 'this_month', label: 'this month', requiresValue: false },
];

export const BOOLEAN_OPERATORS: OperatorConfig[] = [
  { value: 'is_true', label: 'is true', requiresValue: false },
  { value: 'is_false', label: 'is false', requiresValue: false },
];

// Helper to get operators for a field type
export function getOperatorsForType(fieldType: FilterFieldType): OperatorConfig[] {
  switch (fieldType) {
    case 'string':
      return STRING_OPERATORS;
    case 'number':
      return NUMBER_OPERATORS;
    case 'date':
      return DATE_OPERATORS;
    case 'boolean':
      return BOOLEAN_OPERATORS;
    default:
      return STRING_OPERATORS; // Default to string operators
  }
}

// Helper to detect field type from sample values
export function detectFieldType(sampleValues: unknown[]): FilterFieldType {
  if (!sampleValues || sampleValues.length === 0) return 'unknown';

  const sample = sampleValues.find(v => v !== null && v !== undefined && v !== '');
  if (sample === undefined) return 'unknown';

  if (typeof sample === 'boolean') return 'boolean';
  if (typeof sample === 'number') return 'number';
  if (typeof sample === 'string') {
    // Check if it's a date string
    if (/^\d{4}-\d{2}-\d{2}/.test(sample)) return 'date';
    // Check if it's a number string
    if (!isNaN(Number(sample)) && sample.trim() !== '') return 'number';
  }

  return 'string';
}

// Helper to format filter value for display
export function formatFilterValue(condition: FilterCondition): string {
  const { operator, value, value2 } = condition;

  if (operator === 'is_empty' || operator === 'is_not_empty' ||
      operator === 'is_true' || operator === 'is_false' ||
      operator === 'today' || operator === 'this_week' || operator === 'this_month') {
    return '';
  }

  if (operator === 'between' && value2 !== undefined) {
    return `${value} - ${value2}`;
  }

  if (operator === 'last_n_days') {
    return `${value} days`;
  }

  return String(value ?? '');
}

// Helper to get operator label
export function getOperatorLabel(operator: FilterOperator, fieldType: FilterFieldType): string {
  const operators = getOperatorsForType(fieldType);
  const config = operators.find(op => op.value === operator);
  return config?.label ?? operator;
}

// Create a default empty filter condition
export function createEmptyCondition(field?: string, fieldType?: FilterFieldType): FilterCondition {
  return {
    id: `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    field: field ?? '',
    fieldType: fieldType ?? 'string',
    operator: 'equals',
    value: null,
  };
}

// Create a default filter group
export function createEmptyFilterGroup(): FilterGroup {
  return {
    id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    logic: 'and',
    conditions: [createEmptyCondition()],
  };
}

// Create default widget filter config
export function createDefaultFilterConfig(): WidgetFilterConfig {
  return {
    enabled: false,
    groups: [],
    quickFilters: [
      {
        id: 'today',
        label: 'Today',
        icon: 'calendar',
        conditions: [],
      },
      {
        id: 'last_7_days',
        label: '7 Days',
        icon: 'calendar',
        conditions: [],
      },
      {
        id: 'last_30_days',
        label: '30 Days',
        icon: 'calendar',
        conditions: [],
      },
    ],
  };
}
